import AddIcon from '@mui/icons-material/Add';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import { Button, Checkbox, IconButton, Stack, Switch } from '@mui/material';
import React, { useEffect } from 'react';
import { FieldTypeEnum, getAllInvoiceCustomFields, saveInvoiceCustomField } from 'wisestep-api';
import { WsColors, WsDataTable, WsDialogSize } from 'wisestep-base';

import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { useDialog } from '../../../hooks/useDialog';
import { useFetch } from '../../../hooks/useFetch';
import { success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { FieldIconMap } from '../helper/FieldHelper';
import { invoiceSettingsIndexOptions } from '../helper/InvocieSettingsHelper';

import { CustomInvoiceFieldForm } from './CustomInvoiceFieldForm';

export const hardcodedInvoiceFields = [
  {
    label: 'Client Name',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Our Entity',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Invoice Type',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Invoice Number',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Invoice Date',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Due Date',
    type: FieldTypeEnum.URL,
  },
  {
    label: 'Bank Information',
    type: FieldTypeEnum.DateAndTime,
  },
  {
    label: 'Legal Identifier',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Invoice Items',
    type: FieldTypeEnum.Number,
  },
  {
    label: 'Invoice Extras + Computation',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Taxation',
    type: FieldTypeEnum.Location,
  },
  {
    label: 'Additional Taxation',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Terms and Conditions',
    type: FieldTypeEnum.TextBox,
  },
  {
    label: 'Description/Comments',
    type: FieldTypeEnum.TextBox,
  },
].map((field) => ({ ...field, hardcoded: true }));

const mapDTOToField = (field: any) => ({
  ...field,
  ...(field.options && { options: field.options.map((value) => ({ value })) }),
});

const CustomizeInvoiceFields = () => {
  const [openDialog, closeDialog] = useDialog();
  const [fields, setFields] = useFetch(getAllInvoiceCustomFields, []);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const fetchAllInvoiceCustomFields = async () => {
    const { data } = await getAllInvoiceCustomFields();
    setFields(data.map(mapDTOToField));
  };

  useEffect(() => {
    fetchAllInvoiceCustomFields();
  }, []);

  const openCustomFieldForm = (defaultValues?) => {
    openDialog({
      children: (
        <CustomInvoiceFieldForm
          defaultValues={defaultValues}
          onClose={closeDialog}
          onSave={async () => {
            await fetchAllInvoiceCustomFields();
            closeDialog();
          }}
        />
      ),
      size: WsDialogSize.MEDIUM,
    });
  };

  const columns = [
    {
      Header: 'Attribute',
      accessor: 'Attribute',
      Cell: ({ row: { original: row } }) => {
        const Icon = FieldIconMap[row.type];
        return (
          <Stack direction="row" alignItems="center">
            <Icon />
            <span style={{ marginLeft: '1rem' }}>{row.label}</span>
          </Stack>
        );
      },
    },
    {
      Header: 'Visible',
      accessor: 'Visible',
      Cell: ({ row: { original: row } }) =>
        !row.hardcoded && (
          <Switch
            defaultChecked={row.visible}
            onChange={async (_event, checked) => {
              await saveInvoiceCustomField({ ...row, visible: checked });
              setNotification(success(`Custom Field saved successfully!`));
              await fetchAllInvoiceCustomFields();
            }}
          />
        ),
    },
    {
      Header: 'Mandatory',
      accessor: 'Mandatory',
      Cell: ({ row: { original: row } }) =>
        !row.hardcoded && (
          <Checkbox
            defaultChecked={row.required}
            onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
              await saveInvoiceCustomField({ ...row, required: event.target.checked });
              setNotification(success(`Custom Field saved successfully!`));
              await fetchAllInvoiceCustomFields();
            }}
          />
        ),
    },
    {
      Header: '',
      accessor: 'accessor',
      Cell: ({ row: { original: row } }) =>
        !row.hardcoded && (
          <IconButton sx={{ color: WsColors.TheBlues500 }}>
            <BorderColorOutlinedIcon onClick={() => openCustomFieldForm(row)} />
          </IconButton>
        ),
    },
  ];

  return (
    <SettingsPageLayout
      title="Customize Invoice Fields"
      indexOptions={invoiceSettingsIndexOptions}
      breadCrumnbsList={getSettingBreadCrumbList(
        ISettingBreadCrumbTypes.INVOICE,
        Pages['CUSTOMIZE_INVOICE_FIELDS']
      )}
    >
      <Stack spacing={2}>
        <Button
          sx={{ alignSelf: 'flex-end', m: 2 }}
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={() => openCustomFieldForm()}
        >
          Add Custom Field
        </Button>
        <WsDataTable
          columns={columns}
          dense
          data={[...hardcodedInvoiceFields, ...(fields as [])]}
        />
      </Stack>
    </SettingsPageLayout>
  );
};

export default CustomizeInvoiceFields;
